import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  AddNewButton,
  Card,
  Icon,
  MoreButton,
  Search,
} from '@makeably/creativex-design-system';
import TaxonomiesFilter from 'components/creative_campaigns/TaxonomiesFilter';
import TaxonomyModal from 'components/creative_campaigns/TaxonomyModal';
import ChannelLogos from 'components/molecules/ChannelLogos';
import FilterTags from 'components/molecules/FilterTags';
import ItemsTable from 'components/molecules/ItemsTable';
import { addToast } from 'components/organisms/Toasts';
import { optionArrayProps } from 'components/shared';
import { getObjFilterTest } from 'utilities/filtering';
import { getItemSortBy } from 'utilities/item';
import { setItemElement } from 'utilities/itemElement';
import { track } from 'utilities/mixpanel';
import { patch } from 'utilities/requests';
import { toggleActiveTaxonomyCreativeTaxonomyPath } from 'utilities/routes';
import { titleize } from 'utilities/string';
import styles from './Taxonomies.module.css';

export const taxonomyProps = PropTypes.shape({
  active: PropTypes.bool,
  channels: PropTypes.arrayOf(PropTypes.string),
  delimiter: PropTypes.string,
  id: PropTypes.number,
  key: PropTypes.string,
  name: PropTypes.string,
  partners: PropTypes.arrayOf(PropTypes.number),
  position: PropTypes.number,
  taxonomyType: PropTypes.string,
});

const propTypes = {
  canEditTaxonomies: PropTypes.bool.isRequired,
  channelLabelMap: PropTypes.objectOf(PropTypes.string).isRequired,
  channelOptions: optionArrayProps.isRequired,
  partnerNameMap: PropTypes.objectOf(PropTypes.string).isRequired,
  partnerOptions: optionArrayProps.isRequired,
  taxonomies: PropTypes.arrayOf(taxonomyProps).isRequired,
};

const NUM_TAXONOMIES_PER_PAGE = 12;

const allHeaders = [
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'channels',
    label: 'Channels',
    sortable: false,
  },
  {
    key: 'partners',
    label: 'Partners',
    sortable: false,
  },
  {
    key: 'type',
    label: 'Type',
  },
  {
    key: 'indicator',
    label: 'Key/Position',
  },
  {
    key: 'delimiter',
    label: 'Delimiter',
    sortable: false,
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'actions',
    label: '',
    sortable: false,
  },
];

const segments = [
  {
    label: 'Type',
    value: 'taxonomyType',
  },
  {
    label: 'Channels',
    value: 'channels',
  },
  {
    label: 'Partners',
    value: 'partners',
  },
  {
    label: 'Status',
    value: 'active',
  },
];

async function changeStatus(id) {
  const response = await patch(toggleActiveTaxonomyCreativeTaxonomyPath(id));
  const result = response.data;
  addToast(result.message, { type: result.success ? 'success' : 'error' });
  window.location.reload();
}

function getActions(taxonomy, handleShowModal) {
  const {
    id,
    active,
  } = taxonomy;

  const options = [{
    label: 'Edit',
    onClick: () => handleShowModal(taxonomy),
  }];
  if (active) {
    options.push({
      label: 'Deactivate',
      onClick: () => changeStatus(id),
    });
  } else {
    options.push({
      label: 'Activate',
      onClick: () => changeStatus(id),
    });
  }
  return (
    <MoreButton
      menuSize="small"
      options={options}
    />
  );
}

function getChannels(channels, channelLabelMap) {
  const channelItems = channels.map((channel) => ({
    label: channelLabelMap[channel],
    value: channel,
  }));

  return {
    element: (
      <ChannelLogos channels={channelItems} shownMax={3} />
    ),
    value: '',
  };
}

function getPartners(partners, partnerNameMap) {
  if (partners.length === Object.keys(partnerNameMap).length) {
    return 'All';
  }
  return partners.map((partner) => partnerNameMap[partner]).join(', ');
}

function getStatus(active) {
  return {
    element: (
      <div className="u-flexRow u-alignCenter u-gap-8">
        <Icon color={active ? 'green' : 'red'} name={active ? 'checkCircle' : 'exclamationCircle'} />
        { active ? 'Active' : 'Inactive' }
      </div>
    ),
    value: active ? 'active' : 'inactive',
  };
}

function getItems(taxonomies, channelLabelMap, partnerNameMap, handleShowModal) {
  return taxonomies.map((taxonomy) => setItemElement({
    actions: {
      element: getActions(taxonomy, handleShowModal),
      value: taxonomy.id,
    },
    channels: getChannels(taxonomy.channels, channelLabelMap),
    delimiter: { value: taxonomy.delimiter },
    id: { value: taxonomy.id },
    indicator: { value: taxonomy.key || taxonomy.position },
    name: { value: taxonomy.name },
    partners: { value: getPartners(taxonomy.partners, partnerNameMap) },
    status: getStatus(taxonomy.active),
    type: { value: titleize(taxonomy.taxonomyType) },
  }));
}

function renderEmptyTableContent() {
  return (
    <div className="u-flexRow t-empty u-justifyCenter u-marginTop-16">
      No available taxonomy structures
    </div>
  );
}

function Taxonomies({
  canEditTaxonomies,
  channelLabelMap,
  channelOptions,
  partnerOptions,
  partnerNameMap,
  taxonomies,
}) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({
    asc: true,
    key: 'name',
  });
  const [page, setPage] = useState(1);
  const [modalTaxonomy, setModalTaxonomy] = useState();
  const [taxonomyModalOpen, setTaxonomyModalOpen] = useState(false);

  const headers = canEditTaxonomies ? allHeaders : allHeaders.filter((header) => header.key !== 'actions');

  track('view_creative_taxonomy_configuration');

  const handleShowModal = (taxonomy) => {
    setModalTaxonomy(taxonomy);
    setTaxonomyModalOpen(true);
  };

  const filteredTaxonomies = useMemo(() => {
    const filterTest = getObjFilterTest(currentFilters);
    const filtered = taxonomies.filter(filterTest);
    const term = search.toLowerCase();
    return filtered.filter((taxonomy) => taxonomy.name.toLowerCase().includes(term));
  }, [currentFilters, search]);

  const items = useMemo(() => (
    getItems(filteredTaxonomies, channelLabelMap, partnerNameMap, handleShowModal)
  ), [filteredTaxonomies]);

  const sortedItems = useMemo(() => {
    const byKeyDir = getItemSortBy(sort.key, sort.asc);
    return items.slice().sort(byKeyDir);
  }, [items, sort]);

  return (
    <>
      <Card>
        <div className="u-flexRow u-justifyBetween u-marginBottom-16 u-alignCenter">
          <div className="u-flexRow u-gap-16 u-alignCenter">
            <TaxonomiesFilter
              channelLabelMap={channelLabelMap}
              isOpen={filterOpen}
              partnerNameMap={partnerNameMap}
              segments={segments}
              selections={currentFilters}
              taxonomies={taxonomies}
              onClose={() => setFilterOpen(false)}
              onOpen={() => setFilterOpen(true)}
              onSelectionsChange={setCurrentFilters}
            />
            <Search
              value={search}
              onChange={setSearch}
            />
          </div>
          { canEditTaxonomies && (
            <AddNewButton
              label="Add New"
              onClick={() => handleShowModal()}
            />
          ) }
        </div>
        <FilterTags
          dimensions={segments}
          selections={currentFilters}
          onClick={() => setFilterOpen(true)}
          onRemove={setCurrentFilters}
        />
        <ItemsTable
          className={styles.table}
          emptyTableContent={renderEmptyTableContent()}
          headers={headers}
          items={sortedItems}
          page={page}
          perPage={NUM_TAXONOMIES_PER_PAGE}
          sort={sort}
          onPageChange={setPage}
          onSortChange={setSort}
        />
      </Card>
      <TaxonomyModal
        key={modalTaxonomy?.id}
        channelOptions={channelOptions}
        isOpen={taxonomyModalOpen}
        partnerOptions={partnerOptions}
        taxonomy={modalTaxonomy}
        onClose={() => setTaxonomyModalOpen(false)}
      />
    </>
  );
}

Taxonomies.propTypes = propTypes;

export default Taxonomies;
